/**
 * Telas de credencial: pedido de recuperação, definição de nova senha por link
 * e a troca obrigatória do primeiro acesso.
 *
 * Vivem fora da casca do sistema. As duas primeiras porque quem chega ainda não
 * tem sessão; a terceira porque a sessão que a pessoa tem não alcança mais nada
 * — o servidor recusa toda rota até a senha padrão sair.
 */

import { MIN_PASSWORD_LENGTH, passwordSchema } from '@obras/shared';
import { useNavigate } from '@tanstack/react-router';
import { Circle, CircleCheck, Info, Link2Off, MailCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';

import { AuthLayout } from '../components/AuthLayout.js';
import { Button } from '../components/ui/Button.js';
import { PasswordField, TextField } from '../components/ui/Field.js';
import { FormError, Notice } from '../components/ui/Feedback.js';
import { ApiError, api } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { cn } from '../lib/cn.js';

/** Lê o token da querystring sem depender de rota tipada. */
function tokenFromUrl(): string {
  return new URLSearchParams(window.location.search).get('token') ?? '';
}

/** Orienta sem bloquear: a regra que vale é a do servidor. */
function PasswordHints({ value }: { value: string }) {
  if (value === '') return null;

  const checks = [
    {
      label: `ao menos ${MIN_PASSWORD_LENGTH} caracteres`,
      ok: value.length >= MIN_PASSWORD_LENGTH,
    },
    { label: 'letras e números', ok: /[a-zA-Z]/.test(value) && /\d/.test(value) },
  ];

  return (
    <ul className="-mt-1 flex flex-wrap gap-x-5 gap-y-1">
      {checks.map((check) => {
        const Icone = check.ok ? CircleCheck : Circle;
        return (
          <li
            key={check.label}
            className={cn(
              'flex items-center gap-1.5 text-[12px] transition-colors duration-200',
              check.ok ? 'text-success' : 'text-ink-3',
            )}
          >
            <Icone size={13} strokeWidth={1.75} aria-hidden />
            {check.label}
          </li>
        );
      })}
    </ul>
  );
}

function InvalidLink({ description }: { description: string }) {
  const navigate = useNavigate();

  return (
    <AuthLayout title="Link inválido" description={description} icon={Link2Off}>
      <Button className="w-full" size="lg" onClick={() => void navigate({ to: '/login' })}>
        Ir para o login
      </Button>
    </AuthLayout>
  );
}

export function NewPasswordPage() {
  const navigate = useNavigate();
  const [token] = useState(tokenFromUrl);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    if (password !== confirm) {
      setFieldErrors({ confirm: 'As senhas não coincidem.' });
      return;
    }

    const localCheck = passwordSchema.safeParse(password);
    if (!localCheck.success) {
      setFieldErrors({ password: localCheck.error.issues[0]?.message ?? 'Senha inválida.' });
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (error) {
      if (error instanceof ApiError) {
        setFieldErrors(error.fieldErrors());
        setFormError(error.details ? null : error.message);
      } else {
        setFormError('Não foi possível concluir. Verifique sua conexão.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <AuthLayout
        title="Senha definida"
        description="Você já pode entrar com a nova senha."
        icon={CircleCheck}
      >
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          onClick={() => void navigate({ to: '/login' })}
        >
          Ir para o login
        </Button>
      </AuthLayout>
    );
  }

  if (!token) return <InvalidLink description="Este endereço não traz um token válido." />;

  return (
    <AuthLayout
      title="Definir nova senha"
      description="Ao concluir, todas as sessões abertas nesta conta serão encerradas."
    >
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-4"
        noValidate
      >
        <FormError>{formError}</FormError>

        <PasswordField
          label="Nova senha"
          placeholder="Digite a nova senha"
          required
          autoFocus
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          {...(fieldErrors['password'] ? { error: fieldErrors['password'] } : {})}
        />

        <PasswordHints value={password} />

        <PasswordField
          label="Confirme a senha"
          placeholder="Repita a nova senha"
          required
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          {...(fieldErrors['confirm'] ? { error: fieldErrors['confirm'] } : {})}
        />

        <Button type="submit" variant="primary" size="lg" loading={submitting} className="mt-1">
          Salvar senha
        </Button>
      </form>
    </AuthLayout>
  );
}

export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setSubmitting(true);

    try {
      await api.post('/auth/forgot-password', { email });
    } catch {
      // A resposta é idêntica exista ou não o e-mail; falha de rede também não
      // deve revelar nada. O usuário é orientado a procurar o administrador.
    } finally {
      setSubmitting(false);
      setSent(true);
    }
  }

  if (sent) {
    /*
     * A mensagem não confirma nem nega que o endereço existe: qualquer
     * diferença entre os dois casos vira uma forma de descobrir quem tem conta.
     */
    return (
      <AuthLayout
        title="Solicitação registrada"
        description="Se este e-mail estiver cadastrado, você receberá as instruções em instantes."
        icon={MailCheck}
      >
        <Notice icon={Info}>
          O link vale por uma hora e pode ser usado uma única vez. Não chegando, confira a caixa de
          spam ou procure o administrador do órgão.
        </Notice>

        <Button className="mt-4 w-full" onClick={() => void navigate({ to: '/login' })}>
          Voltar ao login
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Recuperar acesso" description="Informe o e-mail cadastrado no órgão.">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-4"
        noValidate
      >
        <TextField
          label="E-mail"
          type="email"
          placeholder="Digite o e-mail cadastrado"
          required
          autoFocus
          autoComplete="username"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />

        <Button type="submit" variant="primary" size="lg" loading={submitting} className="mt-1">
          Solicitar
        </Button>

        <Button variant="ghost" onClick={() => void navigate({ to: '/login' })}>
          Voltar ao login
        </Button>
      </form>
    </AuthLayout>
  );
}

/**
 * Troca obrigatória do primeiro acesso.
 *
 * Não é uma tela pública: a pessoa está logada. Só que a sessão dela não
 * alcança mais nada enquanto a senha for a padrão — a API recusa toda rota que
 * não seja esta. Por isso ela ocupa a casca de credencial, sem menu lateral:
 * não há para onde ir.
 *
 * A senha atual não é pedida. Foi apresentada no login que criou esta sessão, e
 * é a mesma que o gestor ditou — confirmar não prova nada e só dá chance de
 * errar.
 */
export function ForcedPasswordChangePage() {
  const { user, passwordChanged } = useAuth();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFormError(null);
    setFieldErrors({});

    if (password !== confirm) {
      setFieldErrors({ confirm: 'As senhas não coincidem.' });
      return;
    }

    const localCheck = passwordSchema.safeParse(password);
    if (!localCheck.success) {
      setFieldErrors({ password: localCheck.error.issues[0]?.message ?? 'Senha inválida.' });
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/trocar-senha', { newPassword: password });
      setDone(true);
    } catch (error) {
      if (error instanceof ApiError) {
        const porCampo = error.fieldErrors();
        // O servidor reclama de `newPassword`; o campo na tela chama `password`.
        setFieldErrors(porCampo['newPassword'] ? { password: porCampo['newPassword'] } : porCampo);
        setFormError(error.details ? null : error.message);
      } else {
        setFormError('Não foi possível concluir. Verifique sua conexão.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <AuthLayout
        title="Senha alterada"
        description="Entre de novo com a senha que você acabou de escolher."
        icon={CircleCheck}
      >
        <Button variant="primary" size="lg" className="w-full" onClick={passwordChanged}>
          Ir para o login
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Escolha sua senha"
      description={`Você entrou com a senha padrão, que é a mesma para todo mundo. Escolha uma sua para continuar${user ? `, ${user.name.split(' ')[0]}` : ''}.`}
    >
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="flex flex-col gap-4"
        noValidate
      >
        <FormError>{formError}</FormError>

        <PasswordField
          label="Nova senha"
          placeholder="Digite a nova senha"
          required
          autoFocus
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          {...(fieldErrors['password'] ? { error: fieldErrors['password'] } : {})}
        />

        <PasswordHints value={password} />

        <PasswordField
          label="Confirme a senha"
          placeholder="Repita a nova senha"
          required
          autoComplete="new-password"
          value={confirm}
          onChange={(event) => setConfirm(event.target.value)}
          {...(fieldErrors['confirm'] ? { error: fieldErrors['confirm'] } : {})}
        />

        <Button type="submit" variant="primary" size="lg" loading={submitting} className="mt-1">
          Salvar e entrar
        </Button>
      </form>
    </AuthLayout>
  );
}
